/**
 * Nutrition Detection Utility
 * 
 * Detects when the user or AI mentions logging food/nutrition in chat
 * and extracts calories and macros to sync with the nutrition_logs table.
 */

export interface DetectedNutrition {
  calories: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  mealType?: string;
  foodDescription?: string;
}

// Meal type detection patterns
const MEAL_PATTERNS: { pattern: RegExp; mealType: string }[] = [
  { pattern: /\b(breakfast|morning meal|this morning)\b/i, mealType: 'Breakfast' },
  { pattern: /\b(lunch|midday meal)\b/i, mealType: 'Lunch' },
  { pattern: /\b(dinner|supper|evening meal)\b/i, mealType: 'Dinner' },
  { pattern: /\b(snack|treat)\b/i, mealType: 'Snack' },
  { pattern: /\b(pre[- ]?workout)\b/i, mealType: 'Pre-workout' },
  { pattern: /\b(post[- ]?workout)\b/i, mealType: 'Post-workout' },
];

// Patterns that indicate nutrition is being logged/tracked
const LOGGING_INDICATORS = [
  /\b(log(ged|ging)?|track(ed|ing)?|record(ed|ing)?|add(ed|ing)?|ate|had|consumed|eating|gonna eat|going to eat|i'm having|i'll have)\b/i,
  /\b(that'?s?|here'?s?|got|gives? you)\s+\d+\s*(cal|kcal|calories)/i,
  /\bnutrition\s*(info|information|facts|breakdown|data)/i,
  /\b(total(s|ing)?|comes? to|adds? up to)\s*:?\s*\d+/i,
  /\blet'?s?\s+break\s+(it|this|that)\s+down/i,
];

/**
 * Detect if a message contains nutrition logging intent
 */
export function hasNutritionLoggingIntent(message: string): boolean {
  return LOGGING_INDICATORS.some(pattern => pattern.test(message));
}

/**
 * Extract calories from a message
 * Looks for patterns like "500 calories", "500 cal", "500 kcal", "calories: 500"
 */
function extractCalories(text: string): number | null {
  // Pattern: "X calories" or "X cal" or "X kcal"
  const caloriePatterns = [
    /(\d{2,4})\s*(?:cal(?:orie)?s?|kcal)\b/i,
    /calories?:?\s*(\d{2,4})/i,
    /\bcal(?:orie)?s?:?\s*(\d{2,4})/i,
    /total:?\s*(\d{2,4})\s*(?:cal|kcal)?/i,
    /~?\s*(\d{2,4})\s*(?:cal|kcal|calories)\s*(?:total)?/i,
  ];

  for (const pattern of caloriePatterns) {
    const match = text.match(pattern);
    if (match) {
      const calories = parseInt(match[1]);
      // Sanity check: calories should be between 10 and 5000
      if (calories >= 10 && calories <= 5000) {
        return calories;
      }
    }
  }
  return null;
}

/**
 * Extract protein grams from a message
 * Handles various formats: "19g protein", "19 g of protein", "protein: 19g", "~19g protein"
 */
function extractProtein(text: string): number | null {
  const patterns = [
    /~?\s*(\d{1,3})\s*g?\s*(?:of\s+)?protein/i,
    /protein:?\s*~?\s*(\d{1,3})\s*g?/i,
    /(\d{1,3})\s*g\s+(?:of\s+)?protein/i,
    /protein\s*[:\-–]\s*~?\s*(\d{1,3})\s*g?/i,
    // Match table format: | Protein | 19g |
    /protein\s*\|\s*~?\s*(\d{1,3})\s*g?/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const protein = parseInt(match[1]);
      if (protein >= 1 && protein <= 300) {
        return protein;
      }
    }
  }
  return null;
}

/**
 * Extract carbs grams from a message
 */
function extractCarbs(text: string): number | null {
  const patterns = [
    /~?\s*(\d{1,3})\s*g?\s*(?:of\s+)?carb(?:ohydrate)?s?/i,
    /carb(?:ohydrate)?s?:?\s*~?\s*(\d{1,3})\s*g?/i,
    /(\d{1,3})\s*g\s+(?:of\s+)?carb/i,
    /carb(?:ohydrate)?s?\s*[:\-–]\s*~?\s*(\d{1,3})\s*g?/i,
    // Match table format
    /carb(?:ohydrate)?s?\s*\|\s*~?\s*(\d{1,3})\s*g?/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const carbs = parseInt(match[1]);
      if (carbs >= 1 && carbs <= 500) {
        return carbs;
      }
    }
  }
  return null;
}

/**
 * Extract fat grams from a message
 */
function extractFat(text: string): number | null {
  const patterns = [
    /~?\s*(\d{1,3})\s*g?\s*(?:of\s+)?fat/i,
    /fat:?\s*~?\s*(\d{1,3})\s*g?/i,
    /(\d{1,3})\s*g\s+(?:of\s+)?fat/i,
    /fat\s*[:\-–]\s*~?\s*(\d{1,3})\s*g?/i,
    // Match table format
    /fat\s*\|\s*~?\s*(\d{1,3})\s*g?/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const fat = parseInt(match[1]);
      if (fat >= 1 && fat <= 200) {
        return fat;
      }
    }
  }
  return null;
}

/**
 * Detect meal type from message context
 */
function detectMealType(text: string): string | undefined {
  for (const { pattern, mealType } of MEAL_PATTERNS) {
    if (pattern.test(text)) {
      return mealType;
    }
  }
  return undefined;
}

/**
 * Extract food items from user message
 * Looks for patterns like "I'm gonna eat bacon, eggs, and toast"
 */
function extractFoodItems(text: string): string | undefined {
  // Common food-eating patterns
  const patterns = [
    // "gonna eat/have X, Y, and Z"
    /(?:gonna|going to|will)\s+(?:eat|have)\s+(.+?)(?:\.|$)/i,
    // "I'm eating/having X"
    /i'?m\s+(?:eating|having|gonna have)\s+(.+?)(?:\.|$)/i,
    // "I ate/had X"
    /i\s+(?:ate|had|consumed|just had)\s+(.+?)(?:\.|$)/i,
    // "for breakfast/lunch/dinner, X"
    /for\s+(?:breakfast|lunch|dinner|a snack)[,:]?\s+(.+?)(?:\.|$)/i,
    // "breakfast/lunch is X"
    /(?:breakfast|lunch|dinner|my meal)\s+(?:is|was|will be)\s+(.+?)(?:\.|$)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      let foods = match[1].trim();
      // Clean up - remove trailing punctuation and common words
      foods = foods.replace(/[,\s]+(and|with|plus)\s*$/i, '').trim();
      // Capitalize first letter
      if (foods.length > 2 && foods.length < 100) {
        return foods.charAt(0).toUpperCase() + foods.slice(1);
      }
    }
  }
  return undefined;
}

/**
 * Extract individual food item names from AI breakdown
 * Looks for patterns like "2 eggs", "1 slice of bacon"
 */
function extractFoodListFromAI(text: string): string[] {
  const foods: string[] = [];
  
  // Match patterns like "- 2 large eggs", "• 1 slice bacon", "1. Two eggs"
  const listPatterns = [
    /[-•*]\s*\d*\s*(?:x\s+)?(.+?)(?:\s*[-–:]\s*|\s*\(|\n|$)/gim,
    /^\s*\d+[.):]\s*(.+?)(?:\s*[-–:]\s*|\s*\(|\n|$)/gim,
    /\*\*(.+?)\*\*/g,
  ];

  // Common food indicators
  const foodIndicators = /\b(egg|bacon|toast|bread|butter|milk|cheese|chicken|beef|rice|pasta|salad|fruit|vegetable|yogurt|oatmeal|cereal|sandwich|burger|pizza|fish|salmon|tuna|shrimp|pork|ham|turkey|sausage|pancake|waffle|bagel|muffin|croissant|sourdough|avocado|banana|apple|orange|berry|berries)\b/i;

  for (const pattern of listPatterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const item = match[1]?.trim();
      if (item && item.length > 2 && item.length < 50) {
        // Only add if it looks like food
        if (foodIndicators.test(item)) {
          // Clean up the item
          const cleaned = item
            .replace(/^\d+\s*(?:x\s+)?/i, '')
            .replace(/\*\*/g, '')
            .trim();
          if (cleaned && !foods.includes(cleaned)) {
            foods.push(cleaned);
          }
        }
      }
    }
  }
  
  return foods.slice(0, 5); // Max 5 items
}

/**
 * Detect nutrition from a user message (e.g., "I ate 500 calories for lunch")
 */
export function detectNutritionFromUserMessage(message: string): DetectedNutrition | null {
  // Must have logging intent and calories
  if (!hasNutritionLoggingIntent(message)) {
    return null;
  }

  const calories = extractCalories(message);
  if (!calories) {
    return null;
  }

  return {
    calories,
    protein: extractProtein(message),
    carbs: extractCarbs(message),
    fat: extractFat(message),
    mealType: detectMealType(message),
    foodDescription: extractFoodItems(message),
  };
}

/**
 * Detect nutrition from AI response
 * The AI often provides structured nutrition info that we should track
 */
export function detectNutritionFromAIResponse(message: string): DetectedNutrition | null {
  // AI responses might contain nutrition breakdowns
  // Look for patterns like "That's 500 calories, 30g protein..."
  
  // Check for structured nutrition info (AI often provides this format)
  const structuredPatterns = [
    /(?:that'?s|here'?s|approximately|about|around|roughly|total|comes? to|adds? up to)\s*:?\s*~?\s*(\d{2,4})\s*(?:cal|kcal|calories)/i,
    /(?:logged?|tracking?|recorded?|added?)\s+(\d{2,4})\s*(?:cal|kcal|calories)/i,
    /total\s*(?:for\s+(?:the|this)\s+meal)?:?\s*~?\s*(\d{2,4})\s*(?:cal|kcal|calories)?/i,
    /~?\s*(\d{2,4})\s*(?:cal|kcal|calories)\s+total/i,
  ];

  let calories: number | null = null;
  for (const pattern of structuredPatterns) {
    const match = message.match(pattern);
    if (match) {
      const cal = parseInt(match[1]);
      if (cal >= 10 && cal <= 5000) {
        calories = cal;
        break;
      }
    }
  }

  // Also try generic extraction
  if (!calories) {
    calories = extractCalories(message);
  }

  if (!calories) {
    return null;
  }

  // Extract food items from AI's breakdown
  const foodList = extractFoodListFromAI(message);
  const foodDescription = foodList.length > 0 
    ? foodList.join(', ')
    : undefined;

  return {
    calories,
    protein: extractProtein(message),
    carbs: extractCarbs(message),
    fat: extractFat(message),
    mealType: detectMealType(message),
    foodDescription,
  };
}

/**
 * Check if a message pair (user + AI) indicates nutrition was logged
 * Returns detected nutrition if the AI confirmed/provided nutrition info
 */
export function detectNutritionFromConversation(
  userMessage: string,
  aiResponse: string
): DetectedNutrition | null {
  // First, check if user explicitly mentioned nutrition with calories
  const userNutrition = detectNutritionFromUserMessage(userMessage);
  if (userNutrition) {
    return userNutrition;
  }

  // Next, check if AI provided nutrition info (user might have asked about a food)
  // Only count AI responses that seem to be confirming nutrition for logging
  const confirmationPatterns = [
    /\b(logged?|tracked?|recorded?|added?|got it)\b/i,
    /\b(that'?s|here'?s|gives you|comes to|adds up to)\s+~?\s*\d+/i,
    /nutrition (info|breakdown|facts)/i,
    /let'?s?\s+break\s+(it|this|that)\s+down/i,
    /total\s+for\s+(the|this|that)\s+meal/i,
  ];

  const isConfirmation = confirmationPatterns.some(p => p.test(aiResponse));
  
  if (isConfirmation) {
    const aiNutrition = detectNutritionFromAIResponse(aiResponse);
    if (aiNutrition) {
      // Get meal type from user message if not in AI response
      if (!aiNutrition.mealType) {
        aiNutrition.mealType = detectMealType(userMessage);
      }
      // Get food description from user message if not extracted from AI
      if (!aiNutrition.foodDescription) {
        aiNutrition.foodDescription = extractFoodItems(userMessage);
      }
      return aiNutrition;
    }
  }

  return null;
}
